import type { Ref } from "react";
import {
  Box,
  Checkbox,
  Fieldset,
  FieldsetLegend,
  Label,
  Radio,
  RadioGroup,
  Text,
} from "@salestrak/ui";
import { FULL_PERMISSIONS, PERMISSION_LABELS, type Permissions } from "./permission-badge";

interface PermissionEditProps {
  value: Permissions;
  onChange: (permissions: Permissions) => void;
  /** The form's refusal, shown under the choices after a blocked press. */
  error?: string | null;
  fieldRef?: Ref<HTMLFieldSetElement>;
}

export function PermissionEdit({ value, onChange, error, fieldRef }: PermissionEditProps) {
  const mode = value.all ? "full" : "selective";
  // Allowed, but rarely meant: a key that changes data it cannot read.
  const writesWithoutRead =
    !value.all && !value.read && (value.create || value.update || value.delete);

  function handleModeChange(newMode: string) {
    if (newMode === "full") {
      onChange(FULL_PERMISSIONS);
    } else {
      onChange({ all: false, create: false, read: true, update: false, delete: false });
    }
  }

  function handlePermissionToggle(key: keyof Omit<Permissions, "all">) {
    onChange({ ...value, all: false, [key]: !value[key] });
  }

  return (
    // A Fieldset, not a Field: its legend names the group, and each radio and
    // checkbox keeps its own label. The legend sits on the name label's rung.
    <Fieldset ref={fieldRef}>
      <FieldsetLegend>Permissions</FieldsetLegend>
      {/* Label wraps each control, so the text is both the accessible name and
        * the hit target. */}
      <RadioGroup
        value={mode}
        onValueChange={(next) => handleModeChange(String(next))}
      >
        <Label>
          <Radio value="full" />
          <Text size="sm" weight="normal">Full access</Text>
        </Label>
        <Label>
          <Radio value="selective" />
          <Text size="sm" weight="normal">Only select permissions</Text>
        </Label>
      </RadioGroup>

      {mode === "selective" && (
        <Box direction="column" gap="sm" paddingLeft="lg">
          {PERMISSION_LABELS.map((p) => (
            <Label key={p.key}>
              <Checkbox
                checked={value[p.key]}
                onCheckedChange={() => handlePermissionToggle(p.key)}
              />
              <Text size="sm" weight="normal">{`${p.label} — ${p.description}`}</Text>
            </Label>
          ))}
        </Box>
      )}
      {error ? (
        <Text size="xs" variant="destructive" role="alert">
          {error}
        </Text>
      ) : writesWithoutRead ? (
        <Text size="xs" variant="muted">
          This key can change data but cannot read it.
        </Text>
      ) : null}
    </Fieldset>
  );
}
